import React from 'react';
import { FaTimes, FaUsers, FaBuilding, FaUserTie, FaChartPie, FaThumbsUp, FaThumbsDown, FaMinus } from 'react-icons/fa';
import './StakeholderAnalytics.css';

function StakeholderAnalytics({ isOpen, analytics, onClose }) {
  if (!isOpen || !analytics) return null;

  const { summary, by_category, by_type, by_influence, by_interest, by_attitude, by_project } = analytics;

  const getPercentage = (value, total) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  const renderDonutChart = (data, title, colorMap) => (
    <div className="chart-container">
      <h4>{title}</h4>
      <div className="donut-chart">
        <div className="chart-legend">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="legend-item">
              <div 
                className="legend-color" 
                style={{ backgroundColor: colorMap[key] || '#6c757d' }}
              />
              <span className="legend-label">{key}</span>
              <span className="legend-value">{value}</span>
              <span className="legend-percent">
                ({getPercentage(value, summary.total_stakeholders)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBarChart = (data, title, colorMap) => (
    <div className="chart-container">
      <h4>{title}</h4>
      <div className="bar-chart">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="bar-item">
            <div className="bar-label">{key}</div>
            <div className="bar-container">
              <div 
                className="bar-fill" 
                style={{ 
                  width: `${getPercentage(value, Math.max(...Object.values(data)))}%`,
                  backgroundColor: colorMap[key] || '#6c757d'
                }}
              />
              <span className="bar-value">{value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const categoryColors = {
    internal: '#007bff',
    external: '#28a745'
  };

  const influenceColors = {
    high: '#dc3545',
    medium: '#ffc107',
    low: '#28a745'
  };

  const interestColors = {
    high: '#17a2b8',
    medium: '#fd7e14',
    low: '#20c997'
  };

  const attitudeColors = {
    supporter: '#28a745',
    neutral: '#ffc107',
    resistant: '#dc3545'
  };

  const typeColors = {
    employee: '#007bff',
    contractor: '#6f42c1',
    consultant: '#e83e8c',
    customer: '#fd7e14',
    vendor: '#20c997',
    regulatory: '#dc3545',
    community: '#6c757d',
    partner: '#17a2b8'
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content analytics-modal">
        <div className="modal-header">
          <h3>
            <FaChartPie className="modal-icon" />
            Stakeholder Analytics Dashboard
          </h3>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card total">
              <div className="card-icon">
                <FaUsers />
              </div>
              <div className="card-content">
                <h4>{summary.total_stakeholders}</h4>
                <p>Total Stakeholders</p>
              </div>
            </div>

            <div className="summary-card internal">
              <div className="card-icon">
                <FaUserTie />
              </div>
              <div className="card-content">
                <h4>{summary.internal_stakeholders}</h4>
                <p>Internal Stakeholders</p>
                <small>{getPercentage(summary.internal_stakeholders, summary.total_stakeholders)}%</small>
              </div>
            </div>

            <div className="summary-card external">
              <div className="card-icon">
                <FaBuilding />
              </div>
              <div className="card-content">
                <h4>{summary.external_stakeholders}</h4>
                <p>External Stakeholders</p>
                <small>{getPercentage(summary.external_stakeholders, summary.total_stakeholders)}%</small>
              </div>
            </div>

            <div className="summary-card completion">
              <div className="card-icon">
                <FaChartPie />
              </div>
              <div className="card-content">
                <h4>{summary.avg_profile_completion || 0}%</h4>
                <p>Avg Profile Completion</p>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            {/* Category Distribution */}
            {renderDonutChart(by_category, 'Stakeholder Categories', categoryColors)}

            {/* Type Distribution */}
            {renderBarChart(by_type, 'Stakeholder Types', typeColors)}

            {/* Influence Levels */}
            {renderDonutChart(by_influence, 'Influence Levels', influenceColors)}

            {/* Interest Levels */}
            {renderDonutChart(by_interest, 'Interest Levels', interestColors)}

            {/* Attitude Distribution */}
            <div className="chart-container">
              <h4>Attitude Distribution</h4>
              <div className="attitude-chart">
                {Object.entries(by_attitude).map(([attitude, count]) => (
                  <div key={attitude} className="attitude-item">
                    <div className="attitude-icon">
                      {attitude === 'supporter' && <FaThumbsUp style={{ color: attitudeColors[attitude] }} />}
                      {attitude === 'neutral' && <FaMinus style={{ color: attitudeColors[attitude] }} />}
                      {attitude === 'resistant' && <FaThumbsDown style={{ color: attitudeColors[attitude] }} />}
                    </div>
                    <div className="attitude-info">
                      <span className="attitude-label">{attitude}</span>
                      <span className="attitude-count">{count}</span>
                      <span className="attitude-percent">
                        ({getPercentage(count, summary.total_stakeholders)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Distribution */}
            {by_project && Object.keys(by_project).length > 0 && (
              <div className="chart-container full-width">
                <h4>Stakeholders by Project</h4>
                <div className="project-chart">
                  {Object.entries(by_project).map(([project, count]) => (
                    <div key={project} className="project-item">
                      <div className="project-name">{project}</div>
                      <div className="project-bar-container">
                        <div 
                          className="project-bar"
                          style={{ 
                            width: `${getPercentage(count, Math.max(...Object.values(by_project)))}%`
                          }}
                        />
                        <span className="project-count">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Insights Section */}
          <div className="insights-section">
            <h4>Key Insights</h4>
            <div className="insights-grid">
              <div className="insight-card">
                <h5>Engagement Distribution</h5>
                <p>
                  <strong>{getPercentage(by_influence.high || 0, summary.total_stakeholders)}%</strong> of stakeholders have high influence, 
                  while <strong>{getPercentage(by_interest.high || 0, summary.total_stakeholders)}%</strong> have high interest in projects.
                </p>
              </div>

              <div className="insight-card">
                <h5>Internal vs External</h5>
                <p>
                  Your stakeholder base is <strong>
                    {summary.internal_stakeholders > summary.external_stakeholders ? 'primarily internal' : 'primarily external'}
                  </strong> with a {getPercentage(Math.max(summary.internal_stakeholders, summary.external_stakeholders), summary.total_stakeholders)}% majority.
                </p>
              </div>

              <div className="insight-card">
                <h5>Change Readiness</h5>
                <p>
                  <strong>{getPercentage(by_attitude.supporter || 0, summary.total_stakeholders)}%</strong> supporters vs{' '}
                  <strong>{getPercentage(by_attitude.resistant || 0, summary.total_stakeholders)}%</strong> resistant stakeholders suggests{' '}
                  {(by_attitude.supporter || 0) > (by_attitude.resistant || 0) ? 'positive' : 'challenging'} change readiness.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StakeholderAnalytics; 